import { Search } from "lucide-react";
import { ChangeEvent, useCallback, useEffect, useRef } from "react";
import { squircleObserver } from "corner-smoothing";

interface SearchComponentProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  platformCount?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const SearchComponent = ({ onChange, platformCount, onKeyDown }: SearchComponentProps) => {
  const disconnectRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const squircleRef = useCallback((node: HTMLDivElement | null) => {
    disconnectRef.current?.();
    disconnectRef.current = null;
    if (node) {
      const update = squircleObserver(node, {
        cornerRadius: 14,
        cornerSmoothing: 0.8,
      });
      disconnectRef.current = update.disconnect;
    }
  }, []);

  useEffect(() => {
    // Autofocus with a slight delay for modal animation
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const countLabel = platformCount && platformCount > 0
    ? `Search ${platformCount}+ integrations...`
    : "Search integrations...";

  return (
    <div ref={squircleRef} className="relative w-full rounded-[14px] bg-card hover:bg-card-hover transition-colors duration-150">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        <Search size={16} className="text-muted-foreground" />
      </div>
      <input
        ref={inputRef}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={countLabel}
        className="w-full h-11 pl-10 pr-4 py-2.5 text-[14px] bg-transparent border-transparent rounded-[14px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/50"
      />
    </div>
  );
};
